import { useEffect, useState } from "react";
import { api } from "@/lib/api";
import { Button } from "@/components/ui/button";
import { Textarea } from "@/components/ui/textarea";
import { Input } from "@/components/ui/input";
import { Card } from "@/components/ui/card";
import { toast } from "sonner";
import { useAuth } from "@/lib/auth";
import { RotateCcw, Save, MailCheck } from "lucide-react";

export default function EmailTemplate() {
  const { user } = useAuth();
  const [tpl, setTpl] = useState({ greeting: "", button_text: "" });
  const [busy, setBusy] = useState(false);

  useEffect(() => { api.get("/email-template").then((r) => setTpl(r.data)); }, []);

  const save = async () => {
    setBusy(true);
    try {
      const { data } = await api.patch("/email-template", tpl);
      setTpl(data);
      toast.success("Template saved");
    } catch { toast.error("Save failed"); } finally { setBusy(false); }
  };
  const reset = async () => {
    setBusy(true);
    try {
      const { data } = await api.post("/email-template/reset", {});
      setTpl(data);
      toast.success("Reset to default");
    } catch { toast.error("Reset failed"); } finally { setBusy(false); }
  };
  const test = async () => {
    setBusy(true);
    try {
      await api.post("/email/test", {});
      toast.success("Test email sent to " + user.email);
    } catch (e) { toast.error(e?.response?.data?.detail || "Test failed"); } finally { setBusy(false); }
  };

  return (
    <div className="space-y-6" data-testid="template-page">
      <div>
        <h1 className="font-display font-bold text-3xl md:text-4xl text-slate-900">Email template</h1>
        <p className="text-slate-500 mt-1">Edit the greeting and button text. Everything else stays automatic to protect deliverability.</p>
      </div>

      <div className="grid lg:grid-cols-2 gap-6">
        <Card className="rounded-2xl p-6 space-y-4">
          <div>
            <label className="text-sm font-medium text-slate-700 mb-1.5 block">Greeting</label>
            <Textarea rows={4} value={tpl.greeting || ""} onChange={(e) => setTpl({ ...tpl, greeting: e.target.value })} className="rounded-lg" data-testid="template-greeting" />
            <p className="text-xs text-slate-500 mt-1">Every customer gets the same message — Google policy prohibits filtering.</p>
          </div>
          <div>
            <label className="text-sm font-medium text-slate-700 mb-1.5 block">Button text</label>
            <Input value={tpl.button_text || ""} onChange={(e) => setTpl({ ...tpl, button_text: e.target.value })} className="h-12 rounded-lg" data-testid="template-button" />
          </div>
          <div className="flex flex-wrap gap-2 pt-2">
            <Button onClick={save} disabled={busy} className="rounded-full bg-primary hover:bg-primary/90" data-testid="template-save"><Save className="h-4 w-4 mr-2" />Save</Button>
            <Button onClick={reset} disabled={busy} variant="outline" className="rounded-full" data-testid="template-reset"><RotateCcw className="h-4 w-4 mr-2" />Reset to default</Button>
            <Button onClick={test} disabled={busy} variant="ghost" className="rounded-full" data-testid="template-test"><MailCheck className="h-4 w-4 mr-2" />Send test email</Button>
          </div>
        </Card>

        <Card className="rounded-2xl p-6">
          <div className="text-sm font-semibold text-slate-500 uppercase tracking-wide mb-3">Live preview</div>
          <div className="rounded-xl border border-slate-200 bg-slate-50 p-4" data-testid="template-preview">
            <div className="rounded-xl border border-slate-200 bg-white p-6">
              {user?.logo_data_url ? <img src={user.logo_data_url} alt="logo" className="h-10 mb-3 object-contain" /> : <div className="h-10 w-28 bg-slate-100 rounded mb-3" />}
              <div className="font-display font-bold text-lg text-slate-900 mb-2">How did we do, [First name]?</div>
              <p className="text-sm text-slate-600 mb-2">{tpl.greeting}</p>
              <p className="text-sm text-slate-600 mb-4">Would you take 30 seconds to share your experience on Google? It genuinely helps <strong>{user?.business_name || "your business"}</strong>.</p>
              <div className="my-4"><span className="inline-block bg-primary text-white rounded-full px-5 py-2.5 text-sm font-semibold">{tpl.button_text}</span></div>
            </div>
          </div>
        </Card>
      </div>
    </div>
  );
}
